import React, { useState } from "react";
import styled from "@emotion/styled";
import { object, string, boolean, mixed, ValidationError } from "yup";
import { AccountTypeEnum, isValidEmailAddress } from "@ecologi/shared";
import { TextField } from "../../TextField";
import { Button } from "../../Button";
import { Checkbox } from "../../Checkbox";
import { Typography } from "../../Typography";
import { Select, SelectEvent } from "../../Select/Select";
import {
  FormLayout,
  RowItem,
  RowItems,
} from "../../FormLayout/FormLayout.styles";
import { Step1Props, FormData } from "../types";
import { StepTitle } from "./styles";
import { parseYupErrors, clearFieldError } from "./formUtils";

const SignInPrompt = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  margin-bottom: 12px;
`;

const SignInText = styled(Typography)`
  color: ${({ theme }) => theme.colors.charcoal};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;

const EmailErrorContainer = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmailErrorText = styled(Typography)`
  color: ${({ theme }) => theme.colors.scarlet};
`;

const SignInLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.jungle};
  cursor: pointer;
  text-decoration: underline;
  font-size: inherit;
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.jungleDark};
  }
`;

const InfoContainer = styled.div`
  margin-top: 24px;
`;

const TermsContainer = styled.div`
  margin-top: 16px;
`;

const TermsLink = styled.a`
  color: ${({ theme }) => theme.colors.jungle};
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.jungleDark};
  }
`;

const TermsError = styled(Typography)`
  color: ${({ theme }) => theme.colors.scarlet};
  margin-top: 4px;
  margin-left: 38px;
`;

type Step1FormData = Pick<
  FormData,
  "firstName" | "lastName" | "email" | "accountType"
> & {
  termsAccepted: boolean;
};

const ACCOUNT_TYPE_OPTIONS = [
  { label: "An individual", value: AccountTypeEnum.INDIVIDUAL },
  { label: "A company", value: AccountTypeEnum.BUSINESS },
];

const schema = object().shape({
  firstName: string().required("Please enter your first name"),
  lastName: string().required("Please enter your last name"),
  email: string()
    .email("Please enter a valid email address")
    .required("Please enter your email address"),
  accountType: mixed<AccountTypeEnum>()
    .oneOf(Object.values(AccountTypeEnum))
    .required("Please select an account type"),
  termsAccepted: boolean()
    .oneOf([true], "Please accept the terms and conditions")
    .required("Please accept the terms and conditions"),
});

export const Step1NameEmail = ({
  formData,
  emailError,
  isSubmitting,
  onSignIn,
  onContinue,
}: Step1Props) => {
  const [localData, setLocalData] = useState<Step1FormData>({
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    accountType: formData.accountType,
    termsAccepted: formData.termsAccepted,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleTermsChange = (isSelected: boolean) => {
    setLocalData((prev) => ({ ...prev, termsAccepted: isSelected }));
    setErrors((prev) => clearFieldError(prev, "termsAccepted"));
  };

  const handleChange =
    (field: keyof Step1FormData) => (e: { target: { value: string } }) => {
      setLocalData((prev) => ({ ...prev, [field]: e.target.value }));
      setErrors((prev) => clearFieldError(prev, field));
    };

  const handleAccountTypeChange = (e: SelectEvent) => {
    setLocalData((prev) => ({
      ...prev,
      accountType: e.target.value as AccountTypeEnum,
    }));
    setErrors((prev) => clearFieldError(prev, "accountType"));
  };

  const isFormComplete =
    localData.firstName.trim() !== "" &&
    localData.lastName.trim() !== "" &&
    isValidEmailAddress(localData.email) &&
    !!localData.accountType &&
    localData.termsAccepted;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await schema.validate(localData, { abortEarly: false });
      setErrors({});
      onContinue({
        firstName: localData.firstName,
        lastName: localData.lastName,
        email: localData.email,
        accountType: localData.accountType as AccountTypeEnum,
        termsAccepted: localData.termsAccepted,
      });
    } catch (validationError) {
      setErrors(parseYupErrors(validationError as ValidationError));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <StepTitle use="dashboardTitle2" as="h2">
        Checkout
      </StepTitle>

      <SignInPrompt>
        <SignInText use="body1">Already have an account?</SignInText>
        <Button
          green2
          text="Sign in"
          onClick={(e) => {
            e.preventDefault();
            onSignIn();
          }}
        />
      </SignInPrompt>

      <FormLayout>
        <RowItems>
          <RowItem>
            <TextField
              label="First name"
              name="firstName"
              value={localData.firstName}
              onChange={handleChange("firstName")}
              isDisabled={isSubmitting}
              isInvalid={!!errors.firstName}
              errorText={errors.firstName}
              data-testid="checkout-first-name"
            />
          </RowItem>
          <RowItem>
            <TextField
              label="Last name"
              name="lastName"
              value={localData.lastName}
              onChange={handleChange("lastName")}
              isDisabled={isSubmitting}
              isInvalid={!!errors.lastName}
              errorText={errors.lastName}
              data-testid="checkout-last-name"
            />
          </RowItem>
        </RowItems>
        <RowItem>
          <TextField
            label="Email address"
            name="email"
            type="email"
            value={localData.email}
            onChange={handleChange("email")}
            isDisabled={isSubmitting}
            isInvalid={!!errors.email || !!emailError}
            errorText={errors.email}
            data-testid="checkout-email"
          />
          {emailError && (
            <EmailErrorContainer>
              <EmailErrorText use="label">{emailError}</EmailErrorText>
              <SignInLink type="button" onClick={onSignIn}>
                Sign in instead
              </SignInLink>
            </EmailErrorContainer>
          )}
        </RowItem>
        <RowItem>
          <Select
            label="I am making this purchase as..."
            name="accountType"
            items={ACCOUNT_TYPE_OPTIONS}
            value={localData.accountType}
            onChange={handleAccountTypeChange}
            isDisabled={isSubmitting}
            isInvalid={!!errors.accountType}
            errorText={errors.accountType}
            data-testid="checkout-account-type"
          />
        </RowItem>
      </FormLayout>

      <InfoContainer>
        <Typography use="body2">
          We'll automatically set you up with an Ecologi account. This opts you
          into marketing emails. Manage your preferences anytime.
        </Typography>
      </InfoContainer>

      <TermsContainer>
        <Checkbox
          label="terms-accepted"
          isSelected={localData.termsAccepted}
          onChange={handleTermsChange}
          isDisabled={isSubmitting}
        >
          I agree to the{" "}
          <TermsLink href="/resources/terms-and-conditions" target="_blank">
            Terms & Conditions
          </TermsLink>{" "}
          and{" "}
          <TermsLink href="/resources/privacy-policy" target="_blank">
            Privacy Policy
          </TermsLink>
        </Checkbox>
        {errors.termsAccepted && (
          <TermsError use="label">{errors.termsAccepted}</TermsError>
        )}
      </TermsContainer>

      <ButtonContainer>
        <Button
          primary
          submit
          text="Continue"
          disabled={!isFormComplete || isSubmitting}
          loading={isSubmitting}
          data-testid="checkout-step1-continue"
        />
      </ButtonContainer>
    </form>
  );
};
